import { useCallback, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface AvatarUploadInput {
  username: string;
  fullSizeBlob: Blob;
  thumbnailBlob: Blob;
}

export interface AvatarUploadResult {
  fullSizeCdnUrl: string | null;
  thumbnailCdnUrl: string | null;
}

export interface UseAvatarUploadReturn {
  /** Fire both PUTs in parallel; resolves when both succeed. */
  upload: (input: AvatarUploadInput) => Promise<AvatarUploadResult>;
  loading: boolean;
  error: string | null;
  /**
   * Bumps when an upload succeeds. Append as `?v=${avatarVersion}` to a
   * username-derived avatar URL to bust the browser cache. The CDN cache
   * policy ignores query strings, so this only affects the browser, which
   * is exactly what we need (the underlying S3 object was just replaced).
   */
  avatarVersion: number;
}

/**
 * React Query mutation that uploads both avatar sizes in parallel.
 *
 * Two-PUT non-atomicity is acceptable: on partial failure the local cache
 * version is not bumped, so the editor still reflects the previous state
 * and the user can retry. Worst case a stale 128px lingers until the next
 * successful save — same contract as the existing dfe-avatars endpoints.
 */
export function useAvatarUpload(): UseAvatarUploadReturn {
  const [avatarVersion, setAvatarVersion] = useState<number>(() => Date.now());

  const { mutateAsync, isPending, error } = useMutation<
    AvatarUploadResult,
    Error,
    AvatarUploadInput
  >({
    mutationFn: async ({ username, fullSizeBlob, thumbnailBlob }) => {
      const [full, mini] = await Promise.all([
        api.uploadDfeAvatarFullSize(username, fullSizeBlob),
        api.uploadDfeAvatarThumbnail(username, thumbnailBlob),
      ]);
      return {
        fullSizeCdnUrl: full.cdnUrl,
        thumbnailCdnUrl: mini.cdnUrl,
      };
    },
    onSuccess: () => {
      setAvatarVersion(Date.now());
    },
  });

  // mutateAsync is stable across renders, so this useCallback hands a
  // stable function to consumers (handleSave wraps this in another
  // useCallback whose deps include `upload`).
  const upload = useCallback((input: AvatarUploadInput) => mutateAsync(input), [mutateAsync]);

  return {
    upload,
    loading: isPending,
    error: error ? error.message : null,
    avatarVersion,
  };
}
